import React from 'react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

/**
 * AdSlot — a reusable advertising placeholder.
 *
 * Modes:
 *   - When site-config `adsEnabled` is false, renders nothing. (Pre-launch
 *     adoption mode — site stays clean.)
 *   - When `adsEnabled` is true, renders a styled "Your ad here" box at
 *     one of three standard IAB sizes. Drop-in ready for AdSense / AdMob:
 *     just swap the inner JSX for a real <ins class="adsbygoogle"> tag
 *     once you have an ad-unit ID.
 *
 * Props:
 *   - size: 'leaderboard' (728x90), 'medium-rectangle' (300x250),
 *           'mobile-banner' (320x50). Default 'medium-rectangle'.
 *   - label: optional small caption (e.g., "Sponsored")
 *   - className: extra Tailwind classes for the outer container
 *   - testId: data-testid for the slot
 */

const SIZES = {
  leaderboard: { w: 728, h: 90, label: '728×90' },
  'medium-rectangle': { w: 300, h: 250, label: '300×250' },
  'mobile-banner': { w: 320, h: 50, label: '320×50' },
};

export function AdSlot({
  size = 'medium-rectangle',
  label = 'Your ad here',
  className = '',
  testId = 'ad-slot',
}) {
  const { adsEnabled } = useSiteConfig();
  if (!adsEnabled) return null;
  const dims = SIZES[size] || SIZES['medium-rectangle'];

  return (
    <div
      className={`flex items-center justify-center mx-auto my-3 ${className}`}
      style={{ maxWidth: dims.w }}
      data-testid={testId}
      aria-label="advertisement slot"
    >
      <div
        className="w-full flex flex-col items-center justify-center border border-dashed border-[var(--border-medium)] bg-[var(--hover-bg-subtle)] rounded text-center px-3"
        style={{ height: dims.h }}
      >
        <span className="text-[10px] uppercase tracking-wider text-[var(--text-disabled)]">
          Advertisement
        </span>
        <span className="text-sm font-medium text-[var(--text-secondary)] mt-1">
          {label}
        </span>
        <span className="text-[10px] text-[var(--text-disabled)] mt-1">
          ({dims.label})
        </span>
      </div>
    </div>
  );
}

export default AdSlot;
